/**
 * Moz Authority Snapshots Persistence (Story 20.7, AC #16)
 *
 * Fire-and-forget D1 writer for the `moz_authority_snapshots` table. Called at
 * the END of every successful `data.site.metrics.fetch` call within
 * `fetchMetrics` to build a historical-time-series substrate over Moz V3's
 * snapshot-only API.
 *
 * **Architecture:** the persistence path is `ctx.waitUntil(persistMozSnapshot(...))`
 * wrapped in a try/catch that logs failures but never blocks the data-sync
 * queue. If the queue context doesn't expose `waitUntil` (test paths), we fall
 * back to a plain awaited `Promise.allSettled` so failures are still
 * isolated. Round 5 Acceptance Auditor — the don't-block invariant must hold
 * regardless of context shape.
 *
 * **Dedup contract (AC #16):** the unique index on `(tenantId, integrationId,
 * target, scope, indexId)` collapses duplicate snapshots within a single Moz
 * index period to first-write-wins via `INSERT ... ON CONFLICT DO NOTHING`.
 * The cron may fire `fetchMetrics` multiple times within a month; only the
 * first write per (tenant, integration, target, scope, index) is persisted.
 *
 * **GDPR (ADR-008):** `tenantId` cascade-deletes via the FK constraint on
 * tenant erasure. The `invalidateTenantCache` helper in `cache.ts` is also
 * extended to call `DELETE FROM moz_authority_snapshots WHERE tenant_id = ?`
 * so the snapshot table is purged on account deletion.
 */

/**
 * Minimal D1 binding interface (Cloudflare Workers).
 * Avoids depending on `@cloudflare/workers-types` at this layer; the adapter
 * envelope (`AdapterEnv.DB`) types it as `unknown` and the route layer casts.
 *
 * **Round 2 review fix:** the cache-side `InvalidateTenantD1Binding`
 * interface in `../cache.ts` has the same shape (sans the `meta.changes`
 * field that snapshots needs to detect ON CONFLICT no-ops). We keep the two
 * interfaces structurally compatible — `MozD1Binding` extends the base
 * shape with the additional `meta.changes` return type so snapshots.ts can
 * inspect the insert outcome.
 */
export interface MozD1Binding {
  prepare(query: string): {
    bind(...values: unknown[]): {
      run(): Promise<{ meta?: { changes?: number } }>;
    };
  };
}

// ============================================================================
// Types
// ============================================================================

export interface PersistMozSnapshotInput {
  readonly tenantId: string;
  readonly integrationId: string;
  readonly target: string;
  readonly scope: string;
  readonly indexId: string;
  readonly capturedAt: number; // ms epoch
  readonly domainAuthority: number;
  readonly pageAuthority: number;
  readonly spamScore: number | null;
  readonly brandAuthorityScore: number | null;
  readonly linkPropensity: number | null;
  readonly pagesToPage: number | null;
  readonly rootDomainsToPage: number | null;
}

// ============================================================================
// ULID generator (lexicographically sortable, monotonic per millisecond)
// ============================================================================

const ULID_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/**
 * Minimal ULID generator. Crockford-base32 timestamp + 80-bit random.
 * Sufficient for our id-pk needs (we don't need ULID's monotonic guarantees
 * across calls — the unique index on dedup-key columns enforces uniqueness).
 */
function generateUlid(): string {
  const now = Date.now();
  let timestampPart = "";
  let t = now;
  for (let i = 0; i < 10; i++) {
    timestampPart = ULID_ALPHABET[t % 32]! + timestampPart;
    t = Math.floor(t / 32);
  }
  let randomPart = "";
  const randomBytes = new Uint8Array(16);
  crypto.getRandomValues(randomBytes);
  for (let i = 0; i < 16; i++) {
    randomPart += ULID_ALPHABET[randomBytes[i]! % 32];
  }
  return timestampPart + randomPart;
}

// ============================================================================
// Snapshot persistence
// ============================================================================

/**
 * Insert a snapshot row with `ON CONFLICT DO NOTHING` semantics (AC #16
 * dedup contract). Emits a structured-log event on success or failure but
 * never throws — fire-and-forget by design.
 *
 * @returns true if a row was actually inserted, false if it was a no-op
 *   (the index already had a snapshot for the same dedup key).
 */
export async function persistMozSnapshot(
  db: MozD1Binding,
  input: PersistMozSnapshotInput,
): Promise<boolean> {
  try {
    const id = generateUlid();
    // D1 supports `ON CONFLICT(...)  DO NOTHING` syntax via prepared
    // statement. The unique index on
    // (tenant_id, integration_id, target, scope, index_id) is the conflict
    // target.
    const result = await db
      .prepare(
        `INSERT INTO moz_authority_snapshots (
          id, tenant_id, integration_id, target, scope, index_id,
          captured_at,
          domain_authority, page_authority, spam_score, brand_authority_score,
          link_propensity, pages_to_page, root_domains_to_page,
          created_at
        ) VALUES (
          ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?
        )
        ON CONFLICT(tenant_id, integration_id, target, scope, index_id) DO NOTHING`,
      )
      .bind(
        id,
        input.tenantId,
        input.integrationId,
        input.target,
        input.scope,
        input.indexId,
        input.capturedAt,
        input.domainAuthority,
        input.pageAuthority,
        input.spamScore,
        input.brandAuthorityScore,
        input.linkPropensity,
        input.pagesToPage,
        input.rootDomainsToPage,
        Date.now(),
      )
      .run();

    const inserted = result.meta?.changes !== undefined && result.meta.changes > 0;
    return Boolean(inserted);
  } catch (err) {
    // Fire-and-forget — log but never re-throw. The data-sync queue MUST NOT
    // be blocked by a snapshot-write failure.
    console.warn("[moz_snapshot_write_failed]", {
      tenantId: input.tenantId,
      integrationId: input.integrationId,
      target: input.target,
      scope: input.scope,
      indexId: input.indexId,
      error: err instanceof Error ? err.message : String(err),
    });
    return false;
  }
}

/**
 * Helper that wraps `persistMozSnapshot` in the don't-block invariant per
 * AC #16. If the caller has access to `ctx.waitUntil` (Cloudflare Workers
 * canonical fire-and-forget primitive), this routes through it; otherwise
 * falls back to `Promise.allSettled` so failures are still isolated.
 *
 * @param waitUntil - Optional `ctx.waitUntil` from Cloudflare Workers context.
 * @param db - The D1 binding.
 * @param input - The snapshot to persist.
 */
export function persistMozSnapshotFireAndForget(
  waitUntil: ((promise: Promise<unknown>) => void) | undefined,
  db: MozD1Binding,
  input: PersistMozSnapshotInput,
): void {
  const promise = persistMozSnapshot(db, input).catch((err: unknown) => {
    console.warn("[moz_snapshot_promise_rejected]", {
      tenantId: input.tenantId,
      integrationId: input.integrationId,
      error: err instanceof Error ? err.message : String(err),
    });
  });

  if (typeof waitUntil === "function") {
    try {
      waitUntil(promise);
    } catch {
      // Some test harnesses throw on waitUntil; fall through to the
      // allSettled path below to preserve don't-block semantics.
      void Promise.allSettled([promise]);
    }
  } else {
    void Promise.allSettled([promise]);
  }
}
